/**
 * <bundle-builder-modal>
 * Lightweight, accessible modal used for bundle flows.
 *
 * How to open:
 *   window.dispatchEvent(new CustomEvent('bundle-modal:open', { detail: { nth: 3 } }));
 *
 * Close mechanisms:
 *   - Overlay click
 *   - Close (X) button
 *   - Escape key
 *
 * Attributes:
 *   - nth (number): which selection count opens this modal (e.g., nth="3")
 *
 * @customElement bundle-builder-modal
 * @extends HTMLElement
 */
if (!customElements.get('bundle-builder-modal')) {
  class BundleBuilderModal extends HTMLElement {
    constructor() {
      super();

      // State
      this.nth = 0;
      this.isOpen = false;
      this.lastFocused = null;

      // Element refs (set in connectedCallback)
      this.overlay = null;
      this.dialog = null;
      this.closeBtn = null;

      // Bind handlers
      this._handleOverlayClick = this.close.bind(this);
      this._handleCloseClick = this.close.bind(this);
      this._onOpenEvent = this.onOpenEvent.bind(this);
      this._onKeydown = this.onKeydown.bind(this);
      this._trapFocus = this.trapFocus.bind(this);
      this._onSectionEvent = this.onSectionEvent.bind(this);
    }

    connectedCallback() {
      this.overlay = this.querySelector('[data-overlay]');
      this.dialog = this.querySelector('[data-dialog]');
      this.closeBtn = this.querySelector('[data-close]');
      this.nth = parseInt(this.getAttribute('nth') || '0', 10) || 0;

      if (!this.overlay || !this.dialog || !this.closeBtn) {
        return;
      }

      // Close actions
      this.overlay.addEventListener('click', this._handleOverlayClick);
      this.closeBtn.addEventListener('click', this._handleCloseClick);

      // Open via external event
      window.addEventListener('bundle-modal:open', this._onOpenEvent);

      // Accessibility
      document.addEventListener('keydown', this._onKeydown);
      document.addEventListener('keydown', this._trapFocus, true);

      // Shopify customizer events
      document.addEventListener('shopify:section:select', this._onSectionEvent);
      document.addEventListener(
        'shopify:section:deselect',
        this._onSectionEvent,
      );
    }

    disconnectedCallback() {
      if (this.overlay) {
        this.overlay.removeEventListener('click', this._handleOverlayClick);
      }
      if (this.closeBtn) {
        this.closeBtn.removeEventListener('click', this._handleCloseClick);
      }
      window.removeEventListener('bundle-modal:open', this._onOpenEvent);
      document.removeEventListener('keydown', this._onKeydown);
      document.removeEventListener('keydown', this._trapFocus, true);

      document.removeEventListener(
        'shopify:section:select',
        this._onSectionEvent,
      );
      document.removeEventListener(
        'shopify:section:deselect',
        this._onSectionEvent,
      );
    }

    /**
     * Global opener event handler.
     * Accepts either { nth }.
     * @param {CustomEvent<{ nth?: number }>} e
     */
    onOpenEvent(e) {
      const detail = (e && e.detail) || {};
      const nth = detail.nth;

      // Target by nth if provided
      if (Number.isFinite(nth)) {
        if (nth !== this.nth) {
          return;
        }
      }

      this.open();
    }

    /**
     * Handles section select/deselect from the Theme Editor.
     * Opens on "shopify:section:select", closes on "shopify:section:deselect".
     * @param {{ type: string, detail?: { sectionId?: string } }} e
     */
    onSectionEvent(e) {
      const targetId = e?.detail?.sectionId;
      const myId = this.getAttribute('section-id') || this.getAttribute('id');

      if (!targetId || targetId !== myId) {
        return;
      }

      if (e.type === 'shopify:section:select') {
        this.open();
        return;
      }

      if (e.type === 'shopify:section:deselect') {
        this.close();
        return;
      }
    }

    /**
     * Handles Escape to close when open.
     * @param {KeyboardEvent} e
     */
    onKeydown(e) {
      if (!this.isOpen) {
        return;
      }
      if (e.key === 'Escape' || e.key === 'Esc') {
        e.preventDefault();
        this.close();
      }
    }

    /**
     * Simple focus trap while open.
     * @param {KeyboardEvent} e
     */
    trapFocus(e) {
      if (!this.isOpen || e.key !== 'Tab') {
        return;
      }
      const focusables = this.dialog.querySelectorAll(
        [
          'a[href]',
          'area[href]',
          'button:not([disabled])',
          'input:not([disabled]):not([type=hidden])',
          'select:not([disabled])',
          'textarea:not([disabled])',
          '[tabindex]:not([tabindex="-1"])',
        ].join(','),
      );
      if (!focusables.length) {
        return;
      }
      const first = focusables[0];
      const last = focusables[focusables.length - 1];

      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }

    /**
     * Opens the modal and manages focus/scroll locking.
     */
    open() {
      if (this.isOpen) {
        return;
      }
      this.isOpen = true;
      this.lastFocused = document.activeElement;

      this.classList.add('is-open');
      this.dialog.removeAttribute('aria-hidden');
      this.overlay.setAttribute('aria-hidden', 'false');

      // Focus on the close button
      setTimeout(() => this.closeBtn && this.closeBtn.focus(), 100);

      // Lock page scroll
      document.body.classList.add('modal-no-scroll');
    }

    /**
     * Closes the modal and restores focus/scroll.
     */
    close() {
      if (!this.isOpen) {
        return;
      }
      this.isOpen = false;

      this.classList.remove('is-open');
      this.dialog.setAttribute('aria-hidden', 'true');
      this.overlay.setAttribute('aria-hidden', 'true');

      // Unlock page scroll
      document.body.classList.remove('modal-no-scroll');

      // Restore focus
      if (this.lastFocused && typeof this.lastFocused.focus === 'function') {
        this.lastFocused.focus();
      }
    }
  }

  customElements.define('bundle-builder-modal', BundleBuilderModal);
}
